<template>
  <AdminCompanySectionComponent
    title="Invoicing Config"
    testId="fee-config"
    emptyText="Select a company to configure fees."
    errorText="Invoicing config could not be loaded — saving is disabled so the existing configuration stays unchanged."
    :company="selectedCompany"
    :form="feeConfig"
  >
    <div class="row">
      <div class="col-lg-6" style="max-width: 520px">
        <div class="mb-3">
          <label class="form-label" for="pricingMode">Pricing Mode</label>
          <select
            id="pricingMode"
            v-model="form.pricing_mode"
            class="form-select"
          >
            <option value="PAY_AS_YOU_GO">Pay-as-you-go</option>
            <option value="FLAT_FEE">Flat Fee</option>
          </select>
        </div>

        <div v-for="field in FEE_FIELDS" :key="field.key" class="mb-3">
          <label class="form-label" :for="field.id">{{ field.label }}</label>
          <input
            :id="field.id"
            v-model.number="form[field.key]"
            type="number"
            step="0.0001"
            min="0"
            class="form-control"
            placeholder="Leave empty for default"
          />
          <div class="form-text">
            Leave empty to use the default {{ field.defaultName
            }}{{ defaultSuffix(defaults[field.key], field.unit) }}.
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label" for="solderaCommissionFeePriceCap"
            >Commission Price Cap (EUR/MWh)</label
          >
          <input
            id="solderaCommissionFeePriceCap"
            v-model.number="form.soldera_commission_fee_price_cap"
            type="number"
            step="0.0001"
            min="0"
            class="form-control"
            placeholder="Leave empty for no cap"
          />
          <div class="form-text">
            Sale prices above the cap are commissioned at the cap. Leave empty
            for no cap.
          </div>
        </div>

        <div class="form-check form-switch mb-2">
          <input
            id="skipSolderaCancellationFee"
            v-model="form.skip_soldera_cancellation_fee"
            class="form-check-input"
            type="checkbox"
            role="switch"
          />
          <label class="form-check-label" for="skipSolderaCancellationFee">
            Don't charge Soldera cancellation fee
          </label>
        </div>

        <div class="form-check form-switch mb-3">
          <input
            id="skipExportFee"
            v-model="form.skip_export_fee"
            class="form-check-input"
            type="checkbox"
            role="switch"
          />
          <label class="form-check-label" for="skipExportFee">
            Don't charge export fee
          </label>
        </div>

        <div class="form-check form-switch mb-3">
          <input
            id="skipTransferFee"
            v-model="form.skip_transfer_fee"
            class="form-check-input"
            type="checkbox"
            role="switch"
          />
          <label class="form-check-label" for="skipTransferFee">
            Don't charge transfer fee
          </label>
        </div>
      </div>
    </div>
  </AdminCompanySectionComponent>
</template>

<script setup lang="ts">
import AdminCompanySectionComponent from "@/dashboard/admin/AdminCompanySectionComponent.vue";
import { useCompanySectionForm } from "@/dashboard/admin/useCompanySectionForm";
import type { Company } from "@/types";
import { reactive } from "vue";

interface FeeConfig {
  // Null until the company has a stored row: the GET always returns an editable payload, falling
  // back to the no-config baseline for companies that were never given custom pricing.
  id: number | null;
  company_id: number | null;
  pricing_mode: "PAY_AS_YOU_GO" | "FLAT_FEE";
  cancellation_fee: string | null;
  min_cancellation_fee: string | null;
  soldera_issuance_fee: string | null;
  transfer_fee: string | null;
  soldera_commission_fee_price_cap: string | null;
  skip_soldera_cancellation_fee: boolean;
  skip_export_fee: boolean;
  skip_transfer_fee: boolean;
}

interface FeeDefaults {
  cancellation_fee: string;
  min_cancellation_fee: string;
  soldera_issuance_fee: string;
  soldera_transfer_fee: string;
}

/** GET and PUT /api/admin/company/{id}/fee-config both answer with this shape. */
interface FeeConfigView {
  fee_config: FeeConfig;
  defaults?: FeeDefaults;
}

// Overrides that fall back to a backend-resolved default when left empty
const FEE_FIELDS = [
  {
    key: "cancellation_fee",
    id: "cancellationFee",
    label: "Soldera Cancellation Fee (EUR/MWh)",
    defaultName: "rate",
    unit: "EUR/MWh",
  },
  {
    key: "min_cancellation_fee",
    id: "minCancellationFee",
    label: "Minimum Cancellation Fee (EUR / statement)",
    defaultName: "minimum",
    unit: "EUR",
  },
  {
    key: "soldera_issuance_fee",
    id: "solderaIssuanceFee",
    label: "Soldera Issuance Fee (EUR/MWh)",
    defaultName: "rate",
    unit: "EUR/MWh",
  },
  {
    key: "transfer_fee",
    id: "transferFee",
    label: "Soldera Transfer Fee (EUR/MWh)",
    defaultName: "rate",
    unit: "EUR/MWh",
  },
] as const;

const props = defineProps<{
  selectedCompany: Company | null;
}>();

const form = reactive({
  pricing_mode: "PAY_AS_YOU_GO" as "PAY_AS_YOU_GO" | "FLAT_FEE",
  cancellation_fee: null as number | null,
  min_cancellation_fee: null as number | null,
  soldera_issuance_fee: null as number | null,
  transfer_fee: null as number | null,
  soldera_commission_fee_price_cap: null as number | null,
  skip_soldera_cancellation_fee: false,
  skip_export_fee: false,
  skip_transfer_fee: false,
});

// The fee that applies when the corresponding override is left empty, resolved by the backend
// (GlobalEacPremium -> code fallback).
const defaults = reactive({
  cancellation_fee: null as string | null,
  min_cancellation_fee: null as string | null,
  soldera_issuance_fee: null as string | null,
  transfer_fee: null as string | null,
});

const feeConfig = useCompanySectionForm<FeeConfigView>({
  company: () => props.selectedCompany,
  resource: "fee-config",
  apply: (data) => {
    const config = data.fee_config;
    form.pricing_mode = config.pricing_mode;
    form.cancellation_fee = toNumberOrNull(config.cancellation_fee);
    form.min_cancellation_fee = toNumberOrNull(config.min_cancellation_fee);
    form.soldera_issuance_fee = toNumberOrNull(config.soldera_issuance_fee);
    form.transfer_fee = toNumberOrNull(config.transfer_fee);
    form.soldera_commission_fee_price_cap = toNumberOrNull(
      config.soldera_commission_fee_price_cap
    );
    form.skip_soldera_cancellation_fee = config.skip_soldera_cancellation_fee;
    form.skip_export_fee = config.skip_export_fee;
    form.skip_transfer_fee = config.skip_transfer_fee;
    defaults.cancellation_fee = data.defaults?.cancellation_fee ?? null;
    defaults.min_cancellation_fee = data.defaults?.min_cancellation_fee ?? null;
    defaults.soldera_issuance_fee = data.defaults?.soldera_issuance_fee ?? null;
    defaults.transfer_fee = data.defaults?.soldera_transfer_fee ?? null;
  },
  payload: () => ({
    pricing_mode: form.pricing_mode,
    cancellation_fee: toNumberOrNull(form.cancellation_fee),
    min_cancellation_fee: toNumberOrNull(form.min_cancellation_fee),
    soldera_issuance_fee: toNumberOrNull(form.soldera_issuance_fee),
    transfer_fee: toNumberOrNull(form.transfer_fee),
    soldera_commission_fee_price_cap: toNumberOrNull(
      form.soldera_commission_fee_price_cap
    ),
    skip_soldera_cancellation_fee: form.skip_soldera_cancellation_fee,
    skip_export_fee: form.skip_export_fee,
    skip_transfer_fee: form.skip_transfer_fee,
  }),
  savedMessage: ["Saved", "Fee configuration updated."],
});

/**
 * A fee as a number, or null for "use the default".
 *
 * Handles both directions of the round trip: the API sends a Decimal string, and Vue's
 * v-model.number falls back to the raw string "" when the user clears a number input
 * (parseFloat("") is NaN). Either way the PUT payload is number | null, never "" or NaN.
 */
function toNumberOrNull(
  value: number | string | null | undefined
): number | null {
  const parsed = typeof value === "string" ? parseFloat(value) : value;
  return typeof parsed === "number" && !Number.isNaN(parsed) ? parsed : null;
}

function defaultSuffix(value: string | null, unit: string): string {
  return value !== null ? ` (${parseFloat(value)} ${unit})` : "";
}
</script>
